import React from 'react';
import { Modal, Form, message, Input, Button, Tag } from 'antd';
import commonS from 'styles/common.module.less';
import { Event, ServerEvent } from 'types/calendar';
import { FormFieldEnum, FormInterface } from './types';
import TimeRangerPicker from 'components/form/TimeRangerPicker';
import RepeatsSelect from 'components/form/RepeatsSelect';
import { getUserId } from 'utils/localstore';
import { getTimeStrForGoogleCalandar, getRecurrence, getRecurrenceTypeFromStr } from 'utils/calendar';
import { calendarCreateEvent, CreateEventType, calendarUpdateEvent, UpdateEventType, DeleteEventType, calendarDeleteEvent } from 'api/google';
import dayjs from 'dayjs';
import s from './s.module.less';
import { ColorKey } from 'constants/calendar';
import { EventData } from 'data/calendar';
import useTimezone from 'hooks/useTimezone';
import { SUPPORT_TIME_ZONE } from 'constants/common';
import { getBrowserTimezoneOffset } from 'utils/common';
import type { NotificationInstance } from 'antd/es/notification/interface';

type Props = {
    onCancel:()=>void
    event?:Event //for edit fun
    onSuccess?: ()=>void
    api?:NotificationInstance
};

// 'RRULE:FREQ=WEEKLY;INTERVAL=1;UNTIL=2023-05-31T00:00:00Z'
const AddEventModal = (props:Props) => {
    const { onCancel, event, onSuccess, api } = props;
    const [form] = Form.useForm<FormInterface>();
    const [submiting, setSubmiting] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const [,forceUpdate] = React.useState({});
    const isEdit = !!event;
    //const isRecurringEvent = event?.id?.includes('_20');
    const isRecurringEvent = event?.recurrence && isEdit;
    const [currentDay, setCurrentDay] = React.useState<dayjs.Dayjs | undefined>(undefined);
    const [, myTimezone] = useTimezone();

    let showTimezone = false;
    if (myTimezone?.value) {
        const target = SUPPORT_TIME_ZONE.find((item) => item.dictKey.toLowerCase() === (myTimezone?.value as string).toLowerCase());
        if (target && target.offset !== getBrowserTimezoneOffset()) {
            showTimezone = true;
        }
    }

    const showSuccessNotification = (evt: CreateEventType | UpdateEventType) => {
        const timeLabel = `${dayjs(new Date(evt.start)).format('hh:mm')} - ${dayjs(new Date(evt.end)).format('hh:mm A')}`;
        const title = isEdit ? 'Event updated successfully' : 'Event added successfully';
        onSuccess?.();
        if (!api) {
            message.success(title);
            return;
        }
        api?.success({
            message: title,
            description: (
                <div>
                    <div className={s.notiTitle}>{evt.summary}</div>
                    <div className={s.notiTime}>{timeLabel}</div>
                </div>),
            placement: 'topRight',
            duration: 5,
        });
    };

    const showDeleteNotification = (sumary: string) => {
        onSuccess?.();
        if (!api) {
            message.success('Event deleted successfully');
            return;
        }
        api?.warning({
            message: 'Event deleted successfully',
            description: (
                <div>
                    <div className={s.notiTitle}>{sumary} is deleted</div>
                </div>),
            placement: 'topRight',
            duration: 5,
        });
    };
    const onOk = async () => {
        try {
            setSubmiting(true);
            const formValues = await form.validateFields();
            const recurrence = getRecurrence(formValues.time[0], formValues.repeat);
            const title = formValues.eventName.startsWith('[Klarity]') ? formValues.eventName : `[Klarity] - ${formValues.eventName}`;
            const des = `${title} \n [Guest]:${formValues.guestEmail?.split(',')}`;
            const data:CreateEventType = {
                providerId: getUserId()!,
                description: des,
                email: formValues.guestEmail?.split(','),
                location: formValues.location,
                end: getTimeStrForGoogleCalandar(formValues.time[1], myTimezone.value as string), //'2023-01-05T17:00:00-07:00'
                meetingLink: formValues.meetingLink,
                start: getTimeStrForGoogleCalandar(formValues.time[0], myTimezone.value as string),
                summary: title,
                timeZone: myTimezone?.value as string,
                colorId: ColorKey.event,
            };
            if (recurrence) {
                //https://developers.google.com/calendar/api/guides/recurringevents
                data.recurrence = recurrence;
            }
            let result;
            if (isEdit) {
                const updateData:UpdateEventType = {
                    eventId: event.id,
                    ...data,
                };
                result = await calendarUpdateEvent(updateData!);
            } else {
                result = await calendarCreateEvent(data!);
            }
            if (!result.error) {
                showSuccessNotification(data);
            }
        } catch (e:any) {
            console.error(e);
            if (e.errorFields) {
                //form error
                return;
            }
            const msg = e?.toString?.() || 'data error';
            message.error(msg);
        } finally {
            setSubmiting(false);
        }
    };

    const onDelete = async () => {
        try {
            setDeleting(true);
            const data:DeleteEventType = {
                eventId: event!.id,
            };
            const result = await calendarDeleteEvent(data);
            if (!result.error) {
                showDeleteNotification(event?.summary || 'Event');
            }
        } catch (e:any) {
            console.error(e);
            if (e.errorFields) {
                //form error
                return;
            }
            const msg = e?.toString?.() || 'data error';
            message.error(msg);
        } finally {
            setDeleting(false);
        }
    };

    const onDeleteAll = async () => {
        message.info('Coming soon');
        return;
        try {
            setDeleting(true);
            const data:DeleteEventType = {
                eventId: event!.id,
            };
            const result = await calendarDeleteEvent(data);
            if (!result.error) {
                message.success('Deleted successfully');
                onSuccess?.();
            }
        } catch (e:any) {
            console.error(e);
            if (e.errorFields) {
                //form error
                return;
            }
            const msg = e?.toString?.() || 'data error';
            message.error(msg);
        } finally {
            setDeleting(false);
        }
    };

    React.useEffect(() => {
        if (event) {
            const serverEvent:ServerEvent = EventData.transferGoogleEventToServerEvent(event);
            form.setFieldsValue({
                [FormFieldEnum.EVENT_NAME]: serverEvent.summary,
                [FormFieldEnum.MEETING_LINK]: serverEvent.meetingLink,
                [FormFieldEnum.LOCATION]: serverEvent.location,
                [FormFieldEnum.TIME]: [dayjs(serverEvent.start), dayjs(serverEvent.end)],
                [FormFieldEnum.GUEST_EMAIL]: serverEvent.email ? serverEvent.email.join(',') : '',
                [FormFieldEnum.REPEAT]: getRecurrenceTypeFromStr(serverEvent.recurrence),
            });
            forceUpdate({});
        }
    }, [event]);

    return (
        <Modal
            title={event ? 'Edit event' : 'Add event'}
            closable
            className={commonS.modalFixHeightWrap}
            width="660px"
            destroyOnClose
            onCancel={onCancel}
            okText="Save"
            onOk={onOk}
            open
            footer={
                <div>
                    {/* {
                        isEdit && isRecurringEvent &&
                        <Button
                            onClick={onDeleteAll}
                            loading={deleting}
                            danger
                        >
                            Delete all repeated event
                        </Button>
                    } */}
                    {
                        isEdit &&
                        <Button
                            onClick={onDelete}
                            type="primary"
                            loading={deleting}
                            danger
                        >
                            {isRecurringEvent ? 'Delete all' : 'Delete' }
                        </Button>
                    }
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button loading={submiting} type="primary" onClick={onOk}>Save</Button>
                </div>
            }
        >
            <Form
                form={form}
                name="basic"
                className={commonS.formStyle1}
                initialValues={{ remember: true }}
                autoComplete="off"
                layout="vertical"
            >
                <div className={s.module}>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '320px' }}
                                label="Event Name:"
                                name={[FormFieldEnum.EVENT_NAME]}
                                validateFirst
                                rules={[
                                    { required: true, message: 'Event name is required' },
                                ]}
                            >
                                <Input maxLength={200} />
                            </Form.Item>
                        </div>
                    </div>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '550px' }}
                                label={
                                    <div>
                                        Date and time {showTimezone && <Tag color="red">{myTimezone?.value}</Tag>}:
                                    </div>
                                }
                                name={[FormFieldEnum.TIME]}
                                rules={[{ required: true, message: 'Date and time is required' }]}
                            >
                                <TimeRangerPicker
                                    onChange={(val) => {
                                        setCurrentDay(val[0]);
                                    }}
                                />
                            </Form.Item>
                        </div>
                    </div>

                    {
                        //wait logic for BE
                        (!isEdit || isRecurringEvent || true) &&
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '360px' }}
                                    label="Repeats:"
                                    name={[FormFieldEnum.REPEAT]}
                                    dependencies={[FormFieldEnum.TIME]}
                                >
                                    <RepeatsSelect day={currentDay} />
                                </Form.Item>
                            </div>
                        </div>
                    }
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '360px' }}
                                label="Guest email:"
                                name={[FormFieldEnum.GUEST_EMAIL]}
                                validateFirst
                                rules={[
                                    { required: true, message: 'Guest email is required' },
                                    // { type: 'email', message: 'Please input correct email format' },
                                ]}
                            >
                                <Input maxLength={200} placeholder="Split with comma" />
                            </Form.Item>
                        </div>
                    </div>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '360px' }}
                                label="Meeting link:"
                                name={[FormFieldEnum.MEETING_LINK]}
                                validateFirst
                                rules={[
                                    { required: true, message: 'Meeting link is required' },
                                ]}
                            >
                                <Input maxLength={200} placeholder="Virtual meeting link" />
                            </Form.Item>
                        </div>
                    </div>
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '360px' }}
                                label="Location:"
                                name={[FormFieldEnum.LOCATION]}
                                validateFirst
                                rules={[
                                    { required: true, message: 'Location is required' },
                                ]}
                            >
                                <Input maxLength={200} placeholder="" />
                            </Form.Item>
                        </div>
                    </div>

                </div>
            </Form>
        </Modal>
    );
};

export default AddEventModal;
